import React from 'react';
import './SegmentField.scss';

export type SegmentOptionColor = 'green' | 'yellow';

interface SegmentOption {
  id: string;
  /** The option's text, or an icon (then give it a title). */
  label: React.ReactNode;
  /** The option's tooltip and accessible name, for an icon label. */
  title?: string;
  disabled?: boolean;
  color?: SegmentOptionColor;
}

export type SegmentFieldVariant = 'purple' | 'green';

/** The options' height: 20px in a form, 30px as a page's view switch. */
export type SegmentFieldSize = 'small' | 'large';

// Single-select props
export interface SegmentFieldSingleProps {
  mode: 'single';
  options: SegmentOption[];
  selectedId: string;
  onChange: (id: string) => void;
  className?: string;
  variant?: SegmentFieldVariant;
  size?: SegmentFieldSize;
  disabled?: boolean; // Disables the entire segment field
}

// Multi-select props
export interface SegmentFieldMultiProps {
  mode: 'multi';
  options: SegmentOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  className?: string;
  variant?: SegmentFieldVariant;
  size?: SegmentFieldSize;
  disabled?: boolean; // Disables the entire segment field
}

export type SegmentFieldProps = SegmentFieldSingleProps | SegmentFieldMultiProps;

export function SegmentField(props: SegmentFieldProps) {
  const { options, className = '', variant = 'purple', size = 'small', disabled: fieldDisabled = false } = props;

  const handleClick = (optionId: string, optionDisabled?: boolean) => {
    if (fieldDisabled || optionDisabled) return; // Don't handle clicks on disabled options or disabled field

    if (props.mode === 'single') {
      props.onChange(optionId);
    } else {
      // Multi-select: toggle the option
      const isSelected = props.selectedIds.includes(optionId);
      if (isSelected) {
        props.onChange(props.selectedIds.filter(id => id !== optionId));
      } else {
        props.onChange([...props.selectedIds, optionId]);
      }
    }
  };

  const isActive = (optionId: string): boolean => {
    if (props.mode === 'single') {
      return props.selectedId === optionId;
    } else {
      return props.selectedIds.includes(optionId);
    }
  };

  const variantClass = variant !== 'purple' ? `segment-field--${variant}` : '';
  const disabledClass = fieldDisabled ? 'segment-field--disabled' : '';
  const sizeClass = size === 'large' ? 'segment-field--large' : '';

  const getOptionClasses = (option: SegmentOption): string => {
    const classes = ['segment-field__option'];
    // Always add color class for hover states (default to green if not specified)
    if (option.color) {
      classes.push(`segment-field__option--${option.color}`);
    }
    if (isActive(option.id)) {
      classes.push('segment-field__option--active');
    }
    if (option.disabled || fieldDisabled) {
      classes.push('segment-field__option--disabled');
    }
    return classes.join(' ');
  };

  return (
    <div className={`segment-field ${variantClass} ${sizeClass} ${disabledClass} ${className}`}>
      {options.map((option) => (
        <button
          key={option.id}
          className={getOptionClasses(option)}
          onClick={() => handleClick(option.id, option.disabled)}
          type="button"
          disabled={option.disabled || fieldDisabled}
          aria-label={option.title}
          data-tooltip-id={option.title ? 'main-tooltip' : undefined}
          data-tooltip-content={option.title}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
